import type {
  DragUpdateOptions,
  DragUpdateResult,
} from "@project/emerge-types";
import type * as THREE from "three";
import {
  INTERIOR_MAX_SIZE as MAX_SIZE,
  INTERIOR_MIN_SIZE as MIN_SIZE,
} from "../../constants/dimensionLimits";
import { InteriorType } from "../../enum/InteriorType";
import type { FlightCaseSettings } from "../../interfaces/FlightCaseSettings";
import type { RackInteriorOptions } from "../../interfaces/RackInteriorOptions";
import {
  DEFAULT_TRAY_STRENGTH,
  minimumScaledCaseWidth,
  scaleRowsToCaseWidth,
} from "../../utils/compartmentWidths";
import {
  dimensionFromCornerDelta,
  gridStepFromOptions,
} from "../../utils/dragGrid";
import { scaleToTarget } from "../../utils/scaleToTarget";
import {
  buildBoxCornerConstraint,
  cornerAxisPosition,
  cornerAxisSign,
} from "./cornerGeometry";
import type {
  CaseCornerPosition,
  CornerDragConstraintResult,
  CornerDragHandler,
} from "./types";

/**
 * The case width a corner drag asks for, with every row scaled to it. Rows
 * keep filling the case (the dividers stay, the last compartment takes the
 * rounding), and no compartment drops under the interior minimum.
 */
function cornerWidthUpdate(
  interior: RackInteriorOptions,
  currentWidth: number,
  cornerDelta: number,
  gridStepMm: number,
): { width: number; widths: number[][] } | null {
  const divider = interior.trayMaterial?.strength ?? DEFAULT_TRAY_STRENGTH;
  const storedWidth = typeof interior.width === "number" ? interior.width : 465;
  // Rows without a level are not built; they are kept as they are.
  const levelRows = interior.widths.slice(0, interior.heights.length);
  const unbuiltRows = interior.widths.slice(interior.heights.length);
  const minWidth = minimumScaledCaseWidth(levelRows, divider, MIN_SIZE);
  const requested = dimensionFromCornerDelta(
    currentWidth,
    cornerDelta,
    Math.min(currentWidth, minWidth),
    gridStepMm,
  );
  // A case narrower than that (stored before the rule, or with compartments
  // under the minimum) stays as it is until it moves, then goes straight to
  // the smallest width where every compartment has the minimum.
  const width =
    requested === currentWidth ? requested : Math.max(requested, minWidth);
  const scaled = scaleRowsToCaseWidth(
    levelRows,
    storedWidth,
    width,
    divider,
    MIN_SIZE,
  );
  return scaled ? { width, widths: [...scaled, ...unbuiltRows] } : null;
}

/**
 * Drag handler for RACK interior type.
 * Always enabled - resizes overall dimensions with proportional scaling.
 */
export const RackCornerDragHandler: CornerDragHandler = {
  shouldEnable(settings: FlightCaseSettings): boolean {
    return settings.interiorOptions.type === InteriorType.RACK;
  },

  buildConstraint(
    caseCornerPosition: CaseCornerPosition,
    position: THREE.Vector3,
    innerDimensions: THREE.Vector3,
    settings: FlightCaseSettings,
  ): CornerDragConstraintResult | null {
    const rack = settings.interiorOptions as RackInteriorOptions;

    return buildBoxCornerConstraint(
      caseCornerPosition,
      position,
      "rack",
      {
        currentWidth:
          typeof rack.width === "number" ? rack.width : innerDimensions.x,
        currentHeight: rack.heights?.reduce((sum, h) => sum + h, 0) ?? 0,
        currentDepth: rack.depth ?? innerDimensions.z,
      },
      MIN_SIZE,
      MAX_SIZE,
    );
  },

  updateSettings(
    newPosition: THREE.Vector3,
    settingsContext: Record<string, unknown>,
    currentSettings: FlightCaseSettings,
    options?: DragUpdateOptions,
  ): DragUpdateResult<FlightCaseSettings> | null {
    const mode = settingsContext.mode as "xz" | "y";
    const interior = currentSettings.interiorOptions as RackInteriorOptions;
    const gridStepMm = gridStepFromOptions(options);

    if (mode === "xz") {
      const restX = settingsContext.restX as number | undefined;
      const restZ = settingsContext.restZ as number | undefined;
      const currentWidth = settingsContext.currentWidth as number | undefined;
      const currentDepth = settingsContext.currentDepth as number | undefined;
      if (
        restX === undefined ||
        restZ === undefined ||
        currentWidth === undefined ||
        currentDepth === undefined
      ) {
        return null;
      }

      const xSign = cornerAxisSign(restX);
      const zSign = cornerAxisSign(restZ);
      const widthUpdate = cornerWidthUpdate(
        interior,
        currentWidth,
        xSign * (newPosition.x - restX),
        gridStepMm,
      );
      if (!widthUpdate) return null;
      const newWidth = widthUpdate.width;
      const newDepth = dimensionFromCornerDelta(
        currentDepth,
        zSign * (newPosition.z - restZ),
        MIN_SIZE,
        gridStepMm,
      );

      const snappedPosition = newPosition.clone();
      snappedPosition.x = cornerAxisPosition(
        restX,
        xSign,
        currentWidth,
        newWidth,
      );
      snappedPosition.z = cornerAxisPosition(
        restZ,
        zSign,
        currentDepth,
        newDepth,
      );

      return {
        settingsUpdate: {
          interiorOptions: {
            ...interior,
            width: newWidth,
            depth: newDepth,
            widths: widthUpdate.widths,
          },
        },
        displayValues: {
          primary: {
            label: "W×D",
            value: newWidth,
            unit: `×${newDepth}mm`,
          },
        },
        snappedPosition: [
          snappedPosition.x,
          snappedPosition.y,
          snappedPosition.z,
        ],
      };
    } else {
      const restY = settingsContext.restY as number | undefined;
      const currentHeight = settingsContext.currentHeight as number | undefined;
      if (restY === undefined || currentHeight === undefined) return null;

      const targetHeight = dimensionFromCornerDelta(
        currentHeight,
        newPosition.y - restY,
        MIN_SIZE,
        gridStepMm,
      );

      const actualCurrentHeight = interior.heights.reduce(
        (sum, h) => sum + h,
        0,
      );
      const scaledHeights =
        scaleToTarget(interior.heights, actualCurrentHeight, targetHeight) ??
        interior.heights;

      // Per-tray rounding can land a millimetre or two off the target, so the
      // readout and the snapped corner follow what was actually applied.
      const achievedHeight = scaledHeights.reduce((sum, h) => sum + h, 0);
      const snappedPosition = newPosition.clone();
      snappedPosition.y = cornerAxisPosition(
        restY,
        1,
        currentHeight,
        achievedHeight,
      );

      return {
        settingsUpdate: {
          interiorOptions: {
            ...interior,
            heights: scaledHeights,
          },
        },
        displayValues: {
          primary: {
            label: "H",
            value: achievedHeight,
            unit: "mm",
          },
        },
        snappedPosition: [
          snappedPosition.x,
          snappedPosition.y,
          snappedPosition.z,
        ],
      };
    }
  },
};
